import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Heading, Text, Select, Button, Input } from '../../components/ui'
import { supabase } from '../../lib/supabase'
import { ArrowPathIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../../constants'

export type PasiGptHistoricoUser = { id: string; name: string | null }

interface ChatRow {
  id: number
  subject: string | null
  profile_id: string | null
  created_at: string
  updated_at?: string
  deleted?: boolean
}

interface MessageRow {
  id: number | string
  chat_id: number
  user_message?: string | null
  ai_message?: string | null
  created_at: string
}

type ChatStatusFilter = 'all' | 'active' | 'deleted'

function localDayStart(yyyyMmDd: string): Date {
  const [y, m, d] = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}

function localDayEnd(yyyyMmDd: string): Date {
  const [y, m, d] = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  return new Date(y, m - 1, d, 23, 59, 59, 999)
}

/** Supabase PostgrestError não é instanceof Error; extrair mensagem real. */
function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message
    if (typeof m === 'string' && m.length > 0) return m
  }
  return fallback
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Avatar colorido com iniciais, gerado deterministicamente pelo nome. */
function UserAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name && name !== 'Não associado' ? getInitials(name) : '?'
  const colors = [
    'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-emerald-500',
    'bg-teal-500', 'bg-rose-500', 'bg-amber-500', 'bg-cyan-500',
  ]
  const colorIdx =
    name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length
  const bg = colors[colorIdx]
  const dim = size === 'sm' ? 'w-7 h-7 text-[11px]' : 'w-9 h-9 text-sm'
  return (
    <span
      className={`${dim} ${bg} rounded-full flex items-center justify-center font-semibold text-white shrink-0 select-none`}
      aria-hidden
    >
      {initials}
    </span>
  )
}

function UserChip({
  profileId,
  nameByProfileId,
  size = 'md',
}: {
  profileId: string | null
  nameByProfileId: Map<string, string>
  size?: 'sm' | 'md'
}) {
  const name = profileId
    ? (nameByProfileId.get(profileId) || 'Desconhecido')
    : 'Não associado'

  const textClass = size === 'sm'
    ? 'text-xs text-gray-600 dark:text-gray-300'
    : 'text-sm font-medium text-gray-700 dark:text-gray-200'

  return (
    <div className="flex items-center gap-2 min-w-0">
      <UserAvatar name={name} size={size} />
      <span className={`${textClass} truncate`} title={name}>
        {name}
      </span>
    </div>
  )
}

type PasiGptStats = {
  totalMessages: number
  totalProfiles: number
  usersWhoUsed: number
  topUsers: Array<{ profileId: string; messageCount: number }>
}

const PasiGptHistoricoTab: React.FC = () => {
  const [userIdFilter, setUserIdFilter] = useState<string>('')
  const [chatStatusFilter, setChatStatusFilter] = useState<ChatStatusFilter>('all')
  const [lastMessageDateFrom, setLastMessageDateFrom] = useState<string>('')
  const [lastMessageDateTo, setLastMessageDateTo] = useState<string>('')
  const [chatParticipantUsers, setChatParticipantUsers] = useState<PasiGptHistoricoUser[]>([])
  const [chats, setChats] = useState<ChatRow[]>([])
  const [selectedChat, setSelectedChat] = useState<ChatRow | null>(null)
  const [messages, setMessages] = useState<MessageRow[]>([])
  const [chatsLoading, setChatsLoading] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<PasiGptStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [messageCountByChat, setMessageCountByChat] = useState<Record<number, number>>({})
  const [lastMessageAtByChat, setLastMessageAtByChat] = useState<Record<number, string>>({})

  const loadChatParticipantUsers = useCallback(async () => {
    try {
      const { data: rows, error: rowsErr } = await supabase.from('pasi_gpt_chats').select('profile_id')
      if (rowsErr) throw rowsErr
      const seen = new Set<string>()
      const ids: string[] = []
      for (const r of (rows || []) as { profile_id: string | null }[]) {
        if (r.profile_id && !seen.has(r.profile_id)) {
          seen.add(r.profile_id)
          ids.push(r.profile_id)
        }
      }
      if (ids.length === 0) {
        setChatParticipantUsers([])
        return
      }
      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, name')
        .in('id', ids)
      if (pErr) throw pErr
      const nameById = new Map<string, string | null>(
        (profiles || []).map((p: { id: string; name: string | null }) => [p.id, p.name])
      )
      const list: PasiGptHistoricoUser[] = ids.map((id) => ({
        id,
        name: nameById.get(id) ?? null,
      }))
      list.sort((a, b) => {
        const an = (a.name || '').trim()
        const bn = (b.name || '').trim()
        const c = an.localeCompare(bn, 'pt-BR', { sensitivity: 'base' })
        return c !== 0 ? c : a.id.localeCompare(b.id)
      })
      setChatParticipantUsers(list)
    } catch (e) {
      console.error('Erro ao carregar usuários com conversas:', e)
      setChatParticipantUsers([])
    }
  }, [])

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true)
      const [{ count: profCount, error: profErr }, { data: chatRows, error: chatErr }] =
        await Promise.all([
          supabase.from('profiles').select('id', { count: 'exact', head: true }),
          supabase.from('pasi_gpt_chats').select('id, profile_id'),
        ])
      if (profErr) throw profErr
      if (chatErr) throw chatErr

      const chatIdToProfile = new Map<number, string | null>()
      const usedProfiles = new Set<string>()
      for (const row of (chatRows || []) as { id: number; profile_id: string | null }[]) {
        chatIdToProfile.set(row.id, row.profile_id)
        if (row.profile_id) usedProfiles.add(row.profile_id)
      }

      const countsByProfile = new Map<string, number>()
      let totalMessages = 0
      const pageSize = 1000
      let offset = 0
      for (;;) {
        const { data: msgRows, error: mErr } = await supabase
          .from('pasi_gpt_messages')
          .select('chat_id, user_message, ai_message')
          .range(offset, offset + pageSize - 1)
        if (mErr) throw mErr
        const batch = msgRows || []
        for (const m of batch as {
          chat_id: number
          user_message?: string | null
          ai_message?: string | null
        }[]) {
          if (m.user_message != null && String(m.user_message).trim() !== '') totalMessages += 1
          if (m.ai_message != null && String(m.ai_message).trim() !== '') totalMessages += 1
          const pid = chatIdToProfile.get(m.chat_id)
          if (!pid) continue
          countsByProfile.set(pid, (countsByProfile.get(pid) || 0) + 1)
        }
        if (batch.length < pageSize) break
        offset += pageSize
      }

      const topUsers = Array.from(countsByProfile.entries())
        .map(([profileId, messageCount]) => ({ profileId, messageCount }))
        .sort((a, b) => b.messageCount - a.messageCount)
        .slice(0, 3)

      setStats({
        totalMessages,
        totalProfiles: profCount ?? 0,
        usersWhoUsed: usedProfiles.size,
        topUsers,
      })
    } catch (e) {
      console.error('Erro ao carregar indicadores PASI Gpt:', e)
      setStats(null)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const nameByProfileId = useMemo(() => {
    const m = new Map<string, string>()
    for (const u of chatParticipantUsers) {
      const n = u.name?.trim()
      if (n) m.set(u.id, n)
    }
    return m
  }, [chatParticipantUsers])

  const loadChats = useCallback(async () => {
    try {
      setChatsLoading(true)
      setError(null)
      let q = supabase.from('pasi_gpt_chats').select('*').order('created_at', { ascending: false })

      if (userIdFilter) {
        q = q.eq('profile_id', userIdFilter)
      }

      const { data, error: err } = await q
      if (err) throw err
      const rows = ((data as ChatRow[]) || []).slice()
      const statusFilteredRows = rows.filter((chat) => {
        if (chatStatusFilter === 'active') return chat.deleted !== true
        if (chatStatusFilter === 'deleted') return chat.deleted === true
        return true
      })
      const chatIds = statusFilteredRows.map((r) => r.id)
      let nextCounts: Record<number, number> = {}
      let nextLastMessageAt: Record<number, string> = {}
      if (chatIds.length === 0) {
        setMessageCountByChat({})
        setLastMessageAtByChat({})
      } else {
        const { data: messageRows, error: messageErr } = await supabase
          .from('pasi_gpt_messages')
          .select('chat_id, user_message, ai_message, created_at')
          .in('chat_id', chatIds)
        if (messageErr) throw messageErr
        for (const row of (messageRows || []) as Array<{
          chat_id: number
          user_message?: string | null
          ai_message?: string | null
          created_at: string
        }>) {
          const qtd =
            (row.user_message && row.user_message.trim() ? 1 : 0) +
            (row.ai_message && row.ai_message.trim() ? 1 : 0)
          nextCounts[row.chat_id] = (nextCounts[row.chat_id] || 0) + qtd
          const prev = nextLastMessageAt[row.chat_id]
          if (!prev || new Date(row.created_at).getTime() > new Date(prev).getTime()) {
            nextLastMessageAt[row.chat_id] = row.created_at
          }
        }
        setMessageCountByChat(nextCounts)
        setLastMessageAtByChat(nextLastMessageAt)
      }

      const lastMessageDateFromTs = lastMessageDateFrom ? localDayStart(lastMessageDateFrom).getTime() : null
      const lastMessageDateToTs = lastMessageDateTo ? localDayEnd(lastMessageDateTo).getTime() : null
      const dateFilteredRows = statusFilteredRows.filter((chat) => {
        if (lastMessageDateFromTs == null && lastMessageDateToTs == null) return true
        const lastMessageAt = nextLastMessageAt[chat.id]
        if (!lastMessageAt) return false
        const ts = new Date(lastMessageAt).getTime()
        if (lastMessageDateFromTs != null && ts < lastMessageDateFromTs) return false
        if (lastMessageDateToTs != null && ts > lastMessageDateToTs) return false
        return true
      })

      dateFilteredRows.sort((a, b) => {
        const ad = a.deleted === true ? 1 : 0
        const bd = b.deleted === true ? 1 : 0
        if (ad !== bd) return ad - bd
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
      setChats(dateFilteredRows)
      setSelectedChat(null)
      setMessages([])
    } catch (e: unknown) {
      setError(errorMessage(e, 'Erro ao carregar conversas'))
      setChats([])
      setMessageCountByChat({})
      setLastMessageAtByChat({})
    } finally {
      setChatsLoading(false)
    }
  }, [userIdFilter, chatStatusFilter, lastMessageDateFrom, lastMessageDateTo])

  useEffect(() => {
    void loadChatParticipantUsers()
  }, [loadChatParticipantUsers])

  useEffect(() => {
    void loadStats()
  }, [loadStats])

  useEffect(() => {
    void loadChats()
  }, [loadChats])

  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedChat) {
        setMessages([])
        return
      }
      try {
        setMessagesLoading(true)
        setError(null)
        const { data, error: err } = await supabase
          .from('pasi_gpt_messages')
          .select('*')
          .eq('chat_id', selectedChat.id)
          .order('created_at', { ascending: true })

        if (err) throw err
        setMessages((data as MessageRow[]) || [])
      } catch (e: unknown) {
        setError(errorMessage(e, 'Erro ao carregar mensagens'))
        setMessages([])
      } finally {
        setMessagesLoading(false)
      }
    }

    void loadMessages()
  }, [selectedChat])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2">
            Usuários que mais utilizam
          </Text>
          {statsLoading ? (
            <Text color="muted">Carregando…</Text>
          ) : !stats || stats.topUsers.length === 0 ? (
            <Text size="3xl" className="text-gray-900 dark:text-white font-bold">
              —
            </Text>
          ) : (
            <>
              <ul className="space-y-2">
                {stats.topUsers.map((u, i) => (
                  <li
                    key={u.profileId}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span
                      className="text-gray-700 dark:text-gray-200 truncate min-w-0"
                      title={nameByProfileId.get(u.profileId)}
                    >
                      {i + 1}. {nameByProfileId.get(u.profileId) || 'Nome não disponível'}
                    </span>
                    <span className="shrink-0 text-gray-900 dark:text-white font-semibold tabular-nums">
                      {u.messageCount.toLocaleString('pt-BR')}
                    </span>
                  </li>
                ))}
              </ul>
              <Text size="xs" color="muted" className="mt-2">
                Top 3 por interações (registros na conversa)
              </Text>
            </>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2">
            Utilizaram / usuários totais
          </Text>
          {statsLoading ? (
            <Text size="3xl" className="text-gray-900 dark:text-white font-bold">
              …
            </Text>
          ) : stats ? (
            <>
              <Text size="3xl" className="text-gray-900 dark:text-white font-bold tabular-nums">
                {stats.usersWhoUsed.toLocaleString('pt-BR')} /{' '}
                {stats.totalProfiles.toLocaleString('pt-BR')}
              </Text>
              {stats.totalProfiles > 0 && (
                <Text size="xs" color="muted" className="mt-2">
                  {Math.round((stats.usersWhoUsed / stats.totalProfiles) * 100)}% da base de usuários
                </Text>
              )}
            </>
          ) : (
            <Text size="3xl" className="text-gray-900 dark:text-white font-bold">
              —
            </Text>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2">
            Total de mensagens trocadas
          </Text>
          <Text size="3xl" className="text-gray-900 dark:text-white font-bold tabular-nums">
            {statsLoading
              ? '…'
              : stats != null
                ? stats.totalMessages.toLocaleString('pt-BR')
                : '—'}
          </Text>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Usuário
              </label>
              <Select
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
              >
                <option value="">Todas as conversas</option>
                {chatParticipantUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name?.trim() || 'Nome não disponível'}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status do chat
              </label>
              <Select
                value={chatStatusFilter}
                onChange={(e) => setChatStatusFilter(e.target.value as ChatStatusFilter)}
              >
                <option value="all">Todos</option>
                <option value="active">Ativas</option>
                <option value="deleted">Excluídas</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Última mensagem de
              </label>
              <Input
                type="date"
                value={lastMessageDateFrom}
                onChange={(e) => setLastMessageDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Última mensagem até
              </label>
              <Input
                type="date"
                value={lastMessageDateTo}
                onChange={(e) => setLastMessageDateTo(e.target.value)}
              />
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void loadChatParticipantUsers()
              void loadStats()
              void loadChats()
            }}
            disabled={chatsLoading}
            className="shrink-0"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 inline ${chatsLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <Text className="text-red-600 dark:text-red-400">{error}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden min-h-[420px] flex flex-col md:flex-row">
        <div className="w-full md:w-72 lg:w-80 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 flex flex-col max-h-[55vh] md:max-h-[70vh]">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Conversas
            </Heading>
            <Text size="xs" color="muted" className="mt-1">
              {chatsLoading ? 'Carregando…' : `${chats.length} conversa(s)`}
            </Text>
          </div>
          <div className="flex-1 overflow-y-auto">
            {chatsLoading && chats.length === 0 ? (
              <div className="p-4 text-center">
                <Text color="muted">Carregando conversas…</Text>
              </div>
            ) : chats.length === 0 ? (
              <div className="p-4 text-center">
                <Text color="muted">Nenhuma conversa encontrada.</Text>
              </div>
            ) : (
              chats.map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => setSelectedChat(chat)}
                  className={`w-full text-left p-3 border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    selectedChat?.id === chat.id ? 'bg-gray-100 dark:bg-gray-700' : ''
                  } ${chat.deleted === true ? 'opacity-80' : ''}`}
                >
                  <div className="flex items-start gap-2 justify-between">
                    <Text size="sm" className="font-medium text-gray-900 dark:text-white line-clamp-2 flex-1">
                      {chat.subject || `Chat #${chat.id}`}
                    </Text>
                    {chat.deleted === true && (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
                        Excluída
                      </span>
                    )}
                  </div>
                  <div className="mt-2">
                    <UserChip
                      profileId={chat.profile_id}
                      nameByProfileId={nameByProfileId}
                      size="sm"
                    />
                  </div>
                  <Text size="xs" className="text-gray-400 dark:text-gray-500 mt-1.5 pl-9">
                    {new Date(chat.created_at).toLocaleString('pt-BR')}
                  </Text>
                  <Text size="xs" className="text-gray-400 dark:text-gray-500 mt-1 pl-9">
                    Última mensagem:{' '}
                    {lastMessageAtByChat[chat.id]
                      ? new Date(lastMessageAtByChat[chat.id]).toLocaleString('pt-BR')
                      : '—'}
                  </Text>
                  <Text size="xs" className="text-gray-400 dark:text-gray-500 mt-1 pl-9">
                    {(messageCountByChat[chat.id] || 0).toLocaleString('pt-BR')} mensagem(ns)
                  </Text>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-[320px] max-h-[55vh] md:max-h-[70vh]">
          {!selectedChat ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <Text color="muted">Selecione uma conversa para ver as mensagens.</Text>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
                <Text size="sm" className="font-semibold text-gray-900 dark:text-white line-clamp-2">
                  {selectedChat.subject || `Chat #${selectedChat.id}`}
                </Text>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <UserChip
                    profileId={selectedChat.profile_id}
                    nameByProfileId={nameByProfileId}
                  />
                  <div className="flex items-center gap-2 shrink-0">
                    {selectedChat.deleted === true && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
                        Excluída
                      </span>
                    )}
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                      {new Date(selectedChat.created_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/80 dark:bg-gray-900/40">
                {messagesLoading ? (
                  <div className="flex justify-center py-8">
                    <Text color="muted">Carregando mensagens…</Text>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex justify-center py-8">
                    <Text color="muted">Nenhuma mensagem nesta conversa.</Text>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div key={String(message.id)} className="space-y-4">
                      {message.user_message ? (
                        <div className="flex justify-end">
                          <div
                            className="max-w-[90%] md:max-w-3xl px-4 py-3 rounded-lg"
                            style={{ backgroundColor: BRAND_COLOR }}
                          >
                            <Text size="sm" className="whitespace-pre-wrap text-right text-white">
                              {message.user_message}
                            </Text>
                            <Text size="xs" className="mt-2 text-white text-right opacity-80">
                              {new Date(message.created_at).toLocaleDateString('pt-BR')} às{' '}
                              {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Text>
                          </div>
                        </div>
                      ) : null}
                      {message.ai_message ? (
                        <div className="flex justify-start">
                          <div className="max-w-[90%] md:max-w-3xl px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white">
                            <div className="flex items-start gap-3">
                              <div
                                className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 mt-1"
                                style={{ backgroundColor: BRAND_COLOR }}
                              >
                                <SparklesIcon className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <Text size="sm" className="whitespace-pre-wrap text-left">
                                  {message.ai_message}
                                </Text>
                                <Text size="xs" className="mt-2 text-gray-500 dark:text-gray-400 text-left">
                                  {new Date(message.created_at).toLocaleDateString('pt-BR')} às{' '}
                                  {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PasiGptHistoricoTab
